import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useModal } from '../../../context/ModalContext';
import Button from '../Button/Button';
import styles from './BriefModal.module.css';

const INITIAL_FORM = {
  fullName:    '',
  email:       '',
  company:     '',
  serviceType: '',
  problem:     '',
  audience:    '',
  timeline:    '',
  budget:      '',
  branding:    '',
  referral:    '',
};

const BriefModal = () => {
  const { isOpen, closeModal } = useModal();
  const [form, setForm]     = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  if (!isOpen) return null;

  const set = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        {
          from_name:    form.fullName,
          from_email:   form.email,
          company:      form.company      || 'Not provided',
          service_type: form.serviceType  || 'Not specified',
          problem:      form.problem,
          audience:     form.audience     || 'Not specified',
          timeline:     form.timeline     || 'Not specified',
          budget:       form.budget       || 'Not specified',
          branding:     form.branding     || 'Not specified',
          referral:     form.referral     || 'Not provided',
          to_email:     'hello@kilonovatech.co.za',
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
      );
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleClose = () => {
    closeModal();
    setTimeout(() => {
      setForm(INITIAL_FORM);
      setStatus('idle');
    }, 300);
  };

  return (
    <div className={styles.backdrop} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.modalHeader}>
          <div>
            <p className={styles.modalEyebrow}>New Project</p>
            <h3 className={styles.modalTitle}>
              {status === 'success' ? 'Brief received!' : 'Tell us about your project'}
            </h3>
          </div>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">✕</button>
        </div>

        {/* Success State */}
        {status === 'success' ? (
          <div className={styles.successBody}>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.successText}>
              Thanks, <strong>{form.fullName.split(' ')[0]}</strong>! We've received your brief
              and will be in touch at <strong>{form.email}</strong> shortly.
            </p>
            <Button variant="primary" size="md" onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>

            <div className={styles.formBody}>

              {/* Contact */}
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Contact</span>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Full Name <span className={styles.required}>*</span>
                    </label>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder="Jane Smith"
                      value={form.fullName}
                      onChange={set('fullName')}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Email <span className={styles.required}>*</span>
                    </label>
                    <input
                      className={styles.input}
                      type="email"
                      placeholder="jane@company.com"
                      value={form.email}
                      onChange={set('email')}
                      required
                    />
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>
                    Company <span className={styles.optional}>(optional)</span>
                  </label>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Acme Corp"
                    value={form.company}
                    onChange={set('company')}
                  />
                </div>
              </div>

              {/* Project */}
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Project</span>
                <div className={styles.field}>
                  <label className={styles.label}>Service Type</label>
                  <select className={styles.select} value={form.serviceType} onChange={set('serviceType')}>
                    <option value="">Select a service...</option>
                    <option>Website &amp; UX/UI Design</option>
                    <option>Custom Web Application</option>
                    <option>Mobile App</option>
                    <option>Business Automation</option>
                    <option>Not sure yet</option>
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>
                    What problem are you trying to solve? <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    className={styles.textarea}
                    rows={3}
                    placeholder="Briefly describe your project and the challenge you need help with..."
                    value={form.problem}
                    onChange={set('problem')}
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Target Audience</label>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Who is this for?"
                    value={form.audience}
                    onChange={set('audience')}
                  />
                </div>
              </div>

              {/* Scope */}
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Scope</span>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={styles.label}>Estimated Timeline</label>
                    <select className={styles.select} value={form.timeline} onChange={set('timeline')}>
                      <option value="">Select...</option>
                      <option>ASAP</option>
                      <option>1–3 months</option>
                      <option>3–6 months</option>
                      <option>6+ months</option>
                      <option>Flexible</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Budget Range</label>
                    <select className={styles.select} value={form.budget} onChange={set('budget')}>
                      <option value="">Select...</option>
                      <option>Under R50k</option>
                      <option>R50k – R150k</option>
                      <option>R150k – R500k</option>
                      <option>R500k+</option>
                      <option>Not sure yet</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Extra */}
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Extra</span>
                <div className={styles.field}>
                  <label className={styles.label}>Do you have existing branding or designs?</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input type="radio" name="branding" value="Yes" className={styles.radio} onChange={set('branding')} />
                      Yes
                    </label>
                    <label className={styles.radioLabel}>
                      <input type="radio" name="branding" value="Partial" className={styles.radio} onChange={set('branding')} />
                      Partial
                    </label>
                    <label className={styles.radioLabel}>
                      <input type="radio" name="branding" value="No" className={styles.radio} onChange={set('branding')} />
                      No
                    </label>
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>
                    How did you hear about us? <span className={styles.optional}>(optional)</span>
                  </label>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Google, referral, social media..."
                    value={form.referral}
                    onChange={set('referral')}
                  />
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className={styles.modalFooter}>
              {status === 'error' && (
                <span className={styles.errorText}>Something went wrong — please try again.</span>
              )}
              <button type="button" className={styles.cancelBtn} onClick={handleClose}>Cancel</button>
              <Button variant="primary" size="md" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send Brief →'}
              </Button>
            </div>

          </form>
        )}

      </div>
    </div>
  );
};

export default BriefModal;
